import React, { useState, useEffect, useRef } from 'react';
import { Card, Col, Row, Typography, Statistic, List, Avatar, Tag } from 'antd';
import { UserOutlined, MessageOutlined, AppstoreOutlined, TeamOutlined, CameraOutlined, RiseOutlined, FireOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

function DashboardPage() {
  const [overview, setOverview] = useState({});
  const [growthTrend, setGrowthTrend] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/dashboard');
      setOverview(res.overview || {});
      setGrowthTrend(res.growthTrend || []);
      setRecentUsers(res.recentUsers || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    { title: '注册用户', value: overview.totalUsers, icon: <UserOutlined />, color: '#07C160', suffix: '人' },
    { title: '今日新增', value: overview.todayNewUsers, icon: <RiseOutlined />, color: '#FA9D3B', suffix: '人' },
    { title: '消息总数', value: overview.totalMessages, icon: <MessageOutlined />, color: '#10AEFF', suffix: '条' },
    { title: '今日消息', value: overview.todayMessages, icon: <FireOutlined />, color: '#6467F0', suffix: '条' },
    { title: '群组总数', value: overview.totalGroups, icon: <TeamOutlined />, color: '#FF6B6B', suffix: '个' },
    { title: '朋友圈动态', value: overview.totalMoments, icon: <CameraOutlined />, color: '#FA9D3B', suffix: '条' },
    { title: '小程序', value: overview.totalMiniPrograms, icon: <AppstoreOutlined />, color: '#6467F0', suffix: '个' },
    { title: '本周新增', value: overview.weekNewUsers, icon: <RiseOutlined />, color: '#07C160', suffix: '人' },
  ];

  const maxCount = Math.max(...growthTrend.map(g => g.count), 1);

  return (
    <>
      <Title level={4} style={{ marginBottom: 20 }}>仪表盘</Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {cards.map((card, i) => (
          <Col span={6} key={i} style={{ marginBottom: 16 }}>
            <Card hoverable loading={loading}>
              <Statistic
                title={card.title}
                value={card.value || 0}
                suffix={card.suffix}
                valueStyle={{ color: card.color, fontSize: 24, fontWeight: 'bold' }}
                prefix={card.icon}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* 图表 - 用纯 div 实现简单可视化 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="📈 用户增长趋势 (近7天)" loading={loading}>
            {growthTrend.length > 0 ? (
              <div style={{ padding: '20px 0' }}>
                {growthTrend.map((g, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: '#666' }}>{g.date}</span>
                      <span style={{ fontSize: 12, color: '#07C160', fontWeight: 'bold' }}>+{g.count} 人</span>
                    </div>
                    <div style={{ background: '#f0f0f0', borderRadius: 4, height: 20, overflow: 'hidden' }}>
                      <div style={{
                        background: 'linear-gradient(90deg, #07C160, #52c41a)',
                        height: '100%',
                        width: `${(g.count / maxCount) * 100}%`,
                        borderRadius: 4,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暂无数据</div>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="📊 消息量趋势" loading={loading}>
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 36, color: '#10AEFF', fontWeight: 'bold' }}>{overview.totalMessages || 0}</div>
              <div style={{ color: '#666', marginTop: 8 }}>累计消息总数</div>
              <div style={{ fontSize: 14, color: '#52c41a', marginTop: 16 }}>
                今日 +{overview.todayMessages || 0} 条
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* 最新用户 */}
        <Col span={12}>
          <Card title="🆕 最新注册用户" loading={loading}>
            <List
              dataSource={recentUsers}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar style={{ background: '#07C160' }} icon={<UserOutlined />} />}
                    title={<Text strong>{item.nickname}</Text>}
                    description={<Text type="secondary">{item.phone ? `📱 ${item.phone}` : ''}</Text>}
                  />
                  <Tag>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</Tag>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* 快捷入口 */}
        <Col span={12}>
          <Card title="⚡ 快捷入口" loading={loading}>
            <Row gutter={[12, 12]}>
              {[
                { label: '👥 用户管理', key: 'users', color: '#07C160' },
                { label: '👥 群组管理', key: 'groups', color: '#FF6B6B' },
                { label: '📦 插件管理', key: 'plugins', color: '#6467F0' },
                { label: '📱 小程序管理', key: 'mini-programs', color: '#10AEFF' },
                { label: '💬 消息管理', key: 'messages', color: '#10AEFF' },
                { label: '📷 朋友圈管理', key: 'moments', color: '#FA9D3B' },
                { label: '📊 Tab 配置', key: 'tab-config', color: '#07C160' },
                { label: '⚙️ 运营配置', key: 'operation-config', color: '#FA9D3B' },
                { label: '🔧 应用配置', key: 'config', color: '#6467F0' },
              ].map((link) => (
                <Col span={8} key={link.key}>
                  <Card
                    size="small"
                    hoverable
                    style={{ textAlign: 'center', cursor: 'pointer', border: '1px solid #f0f0f0' }}
                    bodyStyle={{ padding: '12px 8px' }}
                    onClick={() => {
                      const event = new CustomEvent('navigate', { detail: link.key });
                      window.dispatchEvent(event);
                    }}
                  >
                    <div style={{ fontSize: 13, color: link.color, fontWeight: 'bold' }}>{link.label}</div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default DashboardPage;
